import { useState } from 'react';
import {
  EXPIRY_WARNING_DAYS,
  REFRESH_TOKEN_WINDOW_DAYS,
  daysRemaining,
  type TenantConnection,
} from '@domain';
import { Card, PageTitle } from '../components/ui.js';
import type { Translate } from '../i18n/index.js';
import { connectUrl, type ConnectionDetail } from '../api.js';
import { ClientFeatures } from './ClientFeatures.js';
import { connectionStyle, theme } from '../theme.js';

const STATE_ORDER: Record<TenantConnection['state'], number> = {
  REAUTH: 0,
  FAILED: 1,
  EXPIRING: 2,
  OK: 3,
};

const GRID = 'minmax(200px,1.5fr) 130px 150px minmax(170px,1.1fr) 90px 210px';

interface ConnectionsScreenProps {
  tenants: TenantConnection[];
  t: Translate;
  /** Switch to that client and show its connection detail. */
  onOpen: (tenant: TenantConnection) => void;
}

export function ConnectionsScreen({ tenants, t, onOpen }: ConnectionsScreenProps) {
  /*
    What to switch on for the client about to be connected.

    Decided here, before consent, because it has to be: the company row
    does not exist until Xero hands the tenant back, and the Projects
    scope has to be on the consent screen the client is about to read.
    Both start off — a new client gets what was agreed, not what the last
    one happened to get.
  */
  const [newTraining, setNewTraining] = useState(false);
  const [newProjects, setNewProjects] = useState(false);
  const attention = tenants.filter((x) => x.state !== 'OK');
  const headerCellStyle = {
    fontSize: '10.5px',
    fontWeight: 500,
    letterSpacing: '.07em',
    textTransform: 'uppercase' as const,
    color: theme.textSubtle,
  };

  const kpis = [
    { label: 'Connected clients', value: tenants.length, sub: 'Live in the practice account', color: theme.text },
    { label: 'Healthy', value: tenants.filter((x) => x.state === 'OK').length, sub: 'Refreshed in the last 24 hours', color: theme.goodDeep },
    { label: 'Inside 14-day warning', value: tenants.filter((x) => x.state === 'EXPIRING').length, sub: 'Token window closing', color: theme.warnDeep },
    { label: 'Broken', value: tenants.filter((x) => x.state === 'REAUTH' || x.state === 'FAILED').length, sub: 'Client must re-authorise', color: '#8A3230' },
  ];

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1240px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <PageTitle
          title={t('Client connections')}
          sub={t('One Xero connection per client organisation. Refresh tokens rotate on every use and die after 60 days of inactivity.')}
        />

        {/*
          Adding the FIRST organisation had no route through the interface at
          all. The only way to reach consent was a "Re-authorise" button beside
          an existing connection that had already gone wrong — so a practice
          with no connections yet could not make one, which is every practice on
          its first day.

          A plain link, not a button with a click handler: this leaves the
          application entirely and goes to Xero, and a link is the honest way to
          say so. It also means a middle-click opens it in a new tab, which is
          what someone comparing two organisations will try to do.
        */}
        <a
          href={connectUrl({ training: newTraining, projects: newProjects })}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: theme.nav,
            color: '#fff',
            borderRadius: theme.radiusControl,
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          {t('Connect an organisation')}
        </a>
      </div>

      <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '8px', maxWidth: '54em', lineHeight: 1.6 }}>
        {t('Connecting takes you to Xero to sign in and choose one organisation. Xero returns you here, and the connection is stored encrypted before anything else happens.')}
      </div>

      {/*
        The two decisions, beside the button that acts on them.

        Projects is the one that shows on the client's consent screen, so
        it is ticked here or not at all — asking afterwards would mean
        sending them back to Xero a second time. Both can be changed later
        on the client's own connection screen.
      */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          marginTop: '12px',
          fontSize: '12.5px',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={newTraining}
            onChange={(e) => setNewTraining(e.target.checked)}
          />
          {t('Give them the Training section')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={newProjects}
            onChange={(e) => setNewProjects(e.target.checked)}
          />
          {t('Ask for Xero Projects as well')}
        </label>
        {newProjects && (
          <span style={{ color: theme.textMuted, flex: '1 1 260px', lineHeight: 1.5 }}>
            {t('Projects appears on the consent screen they are about to read.')}
          </span>
        )}
      </div>

      <div style={{ height: '18px' }} />

      {attention.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            background: '#F8EDEC',
            border: '1px solid #EBD2D1',
            borderRadius: theme.radiusCard,
            padding: '14px 16px',
            marginBottom: '16px',
          }}
        >
          <span style={{ flex: '0 0 auto', width: '30px', height: '30px', borderRadius: theme.radiusPill, background: '#F5E4E3', display: 'grid', placeItems: 'center' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.bad} strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 8v5" />
              <path d="M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: '#8A3230' }}>
              {attention.length} {t('connections need attention')}
            </div>
            <div style={{ fontSize: '12.5px', color: '#7E590F', marginTop: '2px', lineHeight: 1.55 }}>
              {attention.map((x) => `${t(x.name)} — ${t(connectionStyle(x.state).label)}`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: '14px', marginBottom: '18px' }}>
        {kpis.map((kpi) => (
          <Card key={kpi.label} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: '11.5px', color: theme.textMuted }}>{t(kpi.label)}</div>
            <div style={{ fontSize: '26px', fontWeight: 600, letterSpacing: '-.02em', marginTop: '6px', color: kpi.color }}>{kpi.value}</div>
            <div style={{ fontSize: '11.5px', color: theme.textSubtle, marginTop: '3px' }}>{t(kpi.sub)}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            background: theme.surfaceHeader,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <div style={{ flex: '1 1 200px', minWidth: 0, fontWeight: 600, fontSize: '13px' }}>{t('Per-client status')}</div>
          <div style={{ fontSize: '11.5px', color: theme.textSubtle }}>
            {t('Token age is measured against the 60-day inactivity window')}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: '880px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '12px', padding: '9px 16px', background: theme.surfaceMuted, borderBottom: `1px solid ${theme.dividerSoft}` }}>
              {['Client', 'Status', 'Last successful sync', 'Refresh token age', 'Users', ''].map((label, i) => (
                <span key={i} style={{ ...headerCellStyle, textAlign: i === 5 ? 'end' : 'start' }}>
                  {label ? t(label) : ''}
                </span>
              ))}
            </div>

            {[...tenants]
              .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state])
              .map((tenant) => {
                const conn = connectionStyle(tenant.state);
                const left = daysRemaining(tenant.age);
                const broken = tenant.state === 'REAUTH' || tenant.state === 'FAILED';
                return (
                  <div
                    key={tenant.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: GRID,
                      gap: '12px',
                      alignItems: 'center',
                      padding: '11px 16px',
                      borderBottom: `1px solid ${theme.divider}`,
                      background: broken ? '#F9F9FA' : '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                      <span style={{ flex: '0 0 auto', width: '8px', height: '8px', borderRadius: '50%', background: conn.dot }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t(tenant.name)}
                        </div>
                        <div dir="ltr" style={{ fontFamily: theme.fontMono, fontSize: '10.5px', color: theme.textFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tenant.tenantId}
                        </div>
                      </div>
                    </div>

                    <span
                      style={{
                        background: conn.bg,
                        border: `1px solid ${conn.bd}`,
                        color: conn.fg,
                        borderRadius: theme.radiusPill,
                        padding: '3px 9px',
                        fontSize: '10.5px',
                        fontWeight: 700,
                        letterSpacing: '.05em',
                        whiteSpace: 'nowrap',
                        justifySelf: 'start',
                      }}
                    >
                      {t(conn.label)}
                    </span>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '12.5px' }}>{t(tenant.lastSync)}</div>
                      <div style={{ fontSize: '11px', color: theme.textSubtle }}>{t(tenant.lastSyncSub)}</div>
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: '1 1 60px', height: '5px', borderRadius: theme.radiusPill, background: theme.dividerSoft, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${Math.min(100, Math.round((tenant.age / REFRESH_TOKEN_WINDOW_DAYS) * 100))}%`,
                              height: '100%',
                              borderRadius: theme.radiusPill,
                              background: conn.dot,
                            }}
                          />
                        </div>
                        <span style={{ flex: '0 0 auto', fontFamily: theme.fontMono, fontSize: '11.5px', color: theme.label }}>
                          {tenant.age}/{REFRESH_TOKEN_WINDOW_DAYS}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          marginTop: '3px',
                          color: left <= 0 ? '#8A3230' : left <= EXPIRY_WARNING_DAYS ? theme.warnDeep : theme.textSubtle,
                        }}
                      >
                        {left <= 0 ? t('Window closed — re-authorisation required') : `${left} ${t('days left in the window')}`}
                      </div>
                    </div>

                    <div style={{ fontSize: '12.5px', color: theme.label, minWidth: 0 }}>
                      {tenant.users} {t('users')}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7px' }}>
                      <button
                        onClick={() => onOpen(tenant)}
                        style={{
                          background: theme.surfaceSunken,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '12px',
                          padding: '6px 11px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('Open')}
                      </button>
                      {tenant.state !== 'OK' && (
                        <a
                          href="/api/auth/connect"
                          style={{
                            background: theme.nav,
                            color: '#fff',
                            border: 0,
                            borderRadius: '12px',
                            padding: '6px 11px',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            textDecoration: 'none',
                          }}
                        >
                          {t('Re-authorise')}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div style={{ padding: '12px 16px', borderTop: `1px solid ${theme.dividerSoft}`, color: theme.textMuted, fontSize: '11.5px', lineHeight: 1.6 }}>
          {t('Read from the connection table, not from Xero. Application Insights raises an alert at 14 days remaining and on any failed refresh.')}
        </div>
      </Card>
    </div>
  );
}

/**
 * One company's Xero connection, as it actually is.
 *
 * Every figure here used to be a constant written into this file: an access
 * token that always expired in 27 minutes, a create mode that stopped being
 * true the day documents could be filed as approved, and a list headed
 * "granted scopes" that was really the list the application asks for.
 *
 * That last one was the harmful one. A connection keeps the permissions it was
 * granted on the day it was made — adding one to the application changes
 * nothing about a connection that already exists. So the screen was answering
 * the exact question its reader had, and answering it wrongly.
 */
export function XeroScreen({
  detail,
  loading,
  t,
  canManageFeatures,
  featuresBusy,
  featuresError,
  connectHref,
  onSetFeatures,
  onBack,
}: {
  detail: ConnectionDetail | null;
  loading: boolean;
  t: Translate;
  /** Only the practice decides what a client is given. */
  canManageFeatures: boolean;
  featuresBusy: boolean;
  featuresError: string | null;
  /** Consent, pinned to this company so other clients are untouched. */
  connectHref: string;
  onSetFeatures: (change: { training?: boolean; xeroProjects?: boolean }) => void;
  onBack: () => void;
}) {
  const mono = { fontFamily: theme.fontMono, color: theme.text };

  const access = detail?.accessToken;
  const refresh = detail?.refreshToken;

  const accessValue = !detail
    ? '—'
    : access?.expiresInMinutes === null || access?.expiresInMinutes === undefined
      ? t('Not known')
      : access.expiresInMinutes > 0
        ? `${t('Valid')} · ${t('expires in')} ${access.expiresInMinutes} ${t('minutes')}`
        : t('Expired — it will be renewed on the next request');

  const refreshValue = !refresh?.rotatedAt
    ? '—'
    : `${t('Last renewed')} ${String(refresh.rotatedAt).slice(0, 10)} · ${refresh.daysRemaining} ${t('days left of the')} ${refresh.windowDays}`;

  const rows = [
    { label: t('Organisation'), value: detail?.organisation ?? '—', style: { fontWeight: 600 } },
    { label: t('Access token'), value: accessValue, style: { color: access?.valid ? theme.goodDeep : theme.warnDeep, fontWeight: 600 } },
    { label: t('Refresh token'), value: refreshValue, style: mono },
    {
      label: t('Last renewed'),
      value: detail?.lastRefreshAt ? String(detail.lastRefreshAt).slice(0, 19).replace('T', ' ') : t('Never — still on its first token'),
      style: mono,
    },
    { label: 'Base URL', value: 'api.xero.com/api.xro/2.0', style: mono },
    { label: t('Background sync'), value: t('None — no mirrored copy of the ledger'), style: mono },
  ];

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1000px' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 0,
          padding: 0,
          marginBottom: '10px',
          color: theme.ink,
          fontSize: '12.5px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {t('← All client connections')}
      </button>

      <PageTitle
        title={t('Xero connection')}
        sub={t('One connection per client organisation. The backend owns the tokens; reads happen on demand, never as a background sync.')}
      />
      <div style={{ height: '20px' }} />

      {/*
        What CFOAI gave this client, above the token plumbing, because it
        is the thing somebody opens this screen to change. The reconnect
        notice below it covers the general case; this card carries its own
        for the permission a switch just asked for.
      */}
      {canManageFeatures && detail && (
        <ClientFeatures
          features={detail.features}
          busy={featuresBusy}
          error={featuresError}
          reconnectHref={connectHref}
          needsReconnect={detail.needsReconnect}
          t={t}
          onChange={onSetFeatures}
        />
      )}

      {detail?.needsReconnect && !canManageFeatures && (
        <div
          style={{
            background: '#F8EDEC',
            border: '1px solid #EBD2D1',
            borderRadius: theme.radiusCard,
            padding: '14px 16px',
            marginBottom: '14px',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '13px', color: '#8A3230' }}>
            {t('This connection is missing a permission the application now needs')}
          </div>
          <div style={{ fontSize: '12.5px', color: '#7E590F', marginTop: '4px', lineHeight: 1.6 }}>
            {t('Granted before it was added:')}{' '}
            <span style={{ fontFamily: theme.fontMono }}>{detail.missing.join(', ')}</span>.{' '}
            {t('Connect this organisation again to include it. Nothing is lost by reconnecting.')}
          </div>
          <a
            href="/api/auth/connect"
            style={{
              display: 'inline-block',
              marginTop: '10px',
              background: theme.nav,
              color: '#fff',
              borderRadius: theme.radiusControl,
              padding: '8px 14px',
              fontSize: '12.5px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {t('Reconnect this organisation')}
          </a>
        </div>
      )}

      <Card style={{ marginBottom: '14px' }}>
        <div style={{ padding: '13px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13px', background: theme.surfaceHeader }}>
          {t('Connection')}
        </div>
        {rows.map((row) => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              padding: '11px 16px',
              borderBottom: `1px solid ${theme.divider}`,
              fontSize: '13px',
            }}
          >
            <div style={{ flex: '0 0 180px', color: theme.textMuted }}>{row.label}</div>
            <div style={{ flex: '1 1 260px', minWidth: 0, ...row.style }}>
              {loading ? '…' : row.value}
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <div style={{ padding: '13px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13px', background: theme.surfaceHeader }}>
          {t('Permissions this connection was granted')}
        </div>
        <div style={{ padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
          {detail?.granted === null && !loading && (
            <span style={{ fontSize: '12.5px', color: theme.textMuted }}>
              {t('Not recorded — this connection was made before we kept track. Reconnect to find out.')}
            </span>
          )}
          {(detail?.granted ?? []).map((scope) => (
            <span
              key={scope}
              style={{
                fontFamily: theme.fontMono,
                fontSize: '11.5px',
                background: theme.surfaceSunken,
                border: `1px solid ${theme.border}`,
                borderRadius: theme.radiusPill,
                padding: '5px 11px',
              }}
              dir="ltr"
            >
              {scope}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
}
